import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import * as React from 'react';

import { cn } from '@/utils/styles';

const buttonVariants = cva(
	'inline-flex items-center justify-center whitespace-nowrap rounded text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default:
					'bg-gradient-to-b bg-primary from-[#393E40] to-[#272A2C] text-primary-foreground hover:bg-primary/90',
				amber: 'bg-amber-800 text-white hover:bg-amber-900 hover:text-white',
				amberOutline: 'border border-amber-800 text-amber-800 hover:bg-amber-800 hover:text-white',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline: 'border border-input hover:bg-accent hover:text-accent-foreground',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-slate-100 hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
				blue: 'bg-primary-blue text-primary-blue-foreground hover:bg-primary-blue-hover border border-primary-blue hover:border-primary-blue-hover',
				blueOutline: 'text-blue-800 border-primary-blue border',
				light: 'bg-white text-primary border border-border hover:bg-accentLight',
				icon: 'bg-transparent text-secondary hover:text-primary',
			},
			size: {
				default: 'h-10 px-3 py-2 gap-2 rounded-md',
				sm: 'h-9 rounded-md px-3 gap-2',
				popover: 'px-3 py-2 gap-3 text-sm rounded-md',
				lg: 'h-11 rounded-md px-8 gap-2 text-sm',
				icon: 'h-10 w-10',
				'icon-sm': 'h-7 w-7',
				close: 'h-6 w-6',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	disabledWithSpinner?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, children, variant, size, asChild = false, disabledWithSpinner = false, ...props },
		ref
	) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
				disabled={disabledWithSpinner ? true : props?.disabled}
			>
				{disabledWithSpinner && <Loader2 className="size-4 animate-spin" />}

				{/* Slottable marks the real child, so `asChild` still works alongside the spinner. */}
				<Slottable>{children}</Slottable>
			</Comp>
		);
	}
);
Button.displayName = 'Button';

export { Button, buttonVariants };
